import type { AdvDisLayerSourceInput, AdvDisLayerSource } from '../types';
import { phpJsonInt, phpJsonString, phpJsonTruthy } from './php-json-values';
import { isPhpNumeric } from './php-decimal-values';

const SECTIONS = [
    ['creature_type_arr', 'creature_type', 'Creature type'], ['creature_sub_type_arr', 'creature_type', 'Creature type'],
    ['lineage_arr', 'ancestry', 'Ancestry'], ['class_arr', 'class', 'Class'], ['subclass_arr', 'subclass', 'Subclass'],
    ['background_arr', 'background', 'Background'],
] as const;
const ABILITIES = ['passive_ability', 'full_ability', 'ability', 'quick_ability',
    'attack_ability', 'free_ability', 'reaction_ability', 'opportunity_attack_ability'] as const;

/** Shared selection over resolved reader views; equipment owns surfaced enchantment provenance. */
export function collectAdvDisLayerSources(input: AdvDisLayerSourceInput, numericPresence = false): AdvDisLayerSource[] {
    const scalar = (value: unknown): unknown => value !== null && typeof value === 'object' && Object.prototype.hasOwnProperty.call(value, '0')
        ? (value as Record<string, unknown>)['0'] : value;
    const present = (value: unknown): boolean => numericPresence ? isPhpNumeric(scalar(value)) : phpJsonTruthy(value);
    const described = (source: AdvDisLayerSource, value: unknown): AdvDisLayerSource => numericPresence
        ? { ...source, value: phpJsonInt(scalar(value)) } : source;
    const sources: AdvDisLayerSource[] = [];
    for (const [section, kind, fallback] of SECTIONS) {
        for (const node of input.structured[section] ?? []) {
            if (node !== null && present(node.rootLayer)) sources.push(described({ kind, name: node.itemName ?? fallback, item_id: node.itemId }, node.rootLayer));
        }
    }
    const seenAbilityIds = new Set<string>();
    for (const inventory of input.inventory) {
        if (inventory === null || inventory.equippedToken === '') continue;
        const name = phpJsonString(inventory.inventoryName ?? inventory.node.itemName ?? 'Equipment');
        const itemId = inventory.node.itemId;
        const href = itemId !== null && itemId !== '' ? phpJsonString(itemId) : phpJsonTruthy(inventory.inventoryId) ? inventory.inventoryId : null;
        const thumb = [inventory.pictureUrls.thumbnail, inventory.pictureUrls.presentation, inventory.pictureUrls.item]
            .find((value): value is string => typeof value === 'string' && value !== '') ?? null;
        if (present(inventory.node.layerValue)) {
            sources.push(described({ kind: 'equipment', name, item_id: href, inventory_id: inventory.inventoryId, thumb_url: thumb }, inventory.node.layerValue));
        }
        const lists = [...inventory.enchantmentLists, inventory.legacyEnchantments];
        for (const list of lists) {
            if (list === null) continue;
            for (const entry of list) {
                if (entry === null || !present(entry.node.layerValue) || entry.node.hasTrigger) continue;
                const abilityId = entry.node.itemId;
                if (abilityId !== null && abilityId !== '') seenAbilityIds.add(sourceIdentity(abilityId));
                sources.push(described({ kind: 'equipment', name: `${name} — ${phpJsonString(entry.node.itemName ?? 'Enchantment')}`,
                    item_id: href ?? (abilityId !== null && abilityId !== '' ? phpJsonString(abilityId) : null),
                    inventory_id: inventory.inventoryId, thumb_url: thumb }, entry.node.layerValue));
            }
        }
    }
    for (const bucket of ABILITIES) {
        for (const entry of input.abilities[bucket] ?? []) {
            if (entry === null || entry.passiveConditionsActive === false || !present(entry.node.layerValue) || entry.node.hasTrigger) continue;
            const abilityId = entry.node.itemId ?? entry.node.abilityId;
            if (abilityId !== null && seenAbilityIds.has(sourceIdentity(abilityId))) continue;
            sources.push(described({ kind: 'passive_ability', name: entry.node.itemName ?? entry.node.abilityName ?? 'Ability', item_id: abilityId }, entry.node.layerValue));
        }
    }
    for (const status of input.statuses) {
        const value = status.definition !== null && present(status.definition.layerValue) ? status.definition.layerValue : status.entry.rootLayer;
        if (present(value)) {
            sources.push(described({ kind: 'status_effect', name: status.entry.itemName ?? 'Status Effect', item_id: status.statusId ?? status.entry.itemId }, value));
        }
    }
    for (const inventory of input.legacyEquipped) {
        if (inventory !== null && present(inventory.node.rootLayer)) {
            sources.push(described({ kind: 'equipment', name: inventory.node.itemName ?? 'Equipment', item_id: inventory.inventoryId ?? inventory.node.itemId }, inventory.node.rootLayer));
        }
    }
    return sources;
}

// Preserve numeric JSON identities in legacy vectors without using object properties as a set.
function sourceIdentity(value: unknown): string {
    if (typeof value === 'string') return value;
    if (value == null) return '';
    if (typeof value === 'boolean') return value ? '1' : '0';
    if (typeof value === 'number' && Number.isFinite(value)) {
        const encoded = JSON.stringify(value);
        if (/^-?\d+$/.test(encoded)) {
            const integer = BigInt(encoded);
            if (integer >= -9223372036854775808n && integer <= 9223372036854775807n) return encoded;
        }
        return String(BigInt.asIntN(64, BigInt(Math.trunc(value))));
    }
    throw new TypeError('Source ability identity cannot be used as an array key');
}
